// src/pages/BlogPost.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "../api/api";
import DOMPurify from "dompurify";
import Comments from "../components/Comments";
import { Twitter, Linkedin, Facebook, Link as LinkIcon } from "lucide-react";
import { Helmet } from "react-helmet";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    axios
      .get(`/blog/${slug}`) // ✅ Public endpoint, no token
      .then((res) => setPost(res.data))
      .catch(() => setError("Blog not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <svg
          className="animate-spin h-10 w-10 text-indigo-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold mb-2">{error}</h2>
        <p className="text-gray-500">It seems this blog post doesn’t exist.</p>
      </div>
    );

  const pageUrl = `https://interviewnotebook.com/blogs/${String(post.slug)}`;

  const share = (platform) => {
    const url = encodeURIComponent(pageUrl);
    const title = encodeURIComponent(String(post.title));
    switch (platform) {
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
          "_blank"
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
          "_blank"
        );
        break;
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${url}`,
          "_blank"
        );
        break;
      case "copy":
        navigator.clipboard.writeText(pageUrl);
        alert("Link copied to clipboard!");
        break;
      default:
        break;
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* ✅ Helmet for SEO and OG tags */}
      <Helmet>
        <title>{String(post.title)} | InterviewNotebook</title>
        <meta
          name="description"
          content={String(
            (post.content || "").replace(/<[^>]+>/g, "").slice(0, 150)
          )}
        />
        {post.coverImage && (
          <meta property="og:image" content={String(post.coverImage)} />
        )}
        {post.coverImage && (
          <meta property="twitter:image" content={String(post.coverImage)} />
        )}
        <meta property="og:title" content={String(post.title)} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      {/* ✅ Back Link */}
      <Link
        to="/blogs"
        className="text-indigo-600 hover:underline mb-4 inline-block"
      >
        ← Back to Blogs
      </Link>

      {/* ✅ Blog Title & Date */}
      <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
      <p className="text-sm text-gray-500 mb-4">
        {new Date(post.publishedAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
      </p>

      {/* ✅ Cover Image */}
      {post.coverImage && (
        <img
          src={post.coverImage}
          alt={post.title}
          className="rounded-lg mb-6 w-full object-cover max-h-96 shadow"
          loading="lazy"
        />
      )}

      {/* ✅ Blog Content */}
      <div
        className="prose max-w-none mb-6"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(post.content),
        }}
      />

      {/* ✅ Social Share */}
      <div className="flex gap-3 mb-8">
        <button
          onClick={() => share("twitter")}
          className="p-2 border rounded hover:bg-blue-50 transition"
          aria-label="Share on Twitter"
        >
          <Twitter size={18} />
        </button>
        <button
          onClick={() => share("linkedin")}
          className="p-2 border rounded hover:bg-blue-50 transition"
          aria-label="Share on LinkedIn"
        >
          <Linkedin size={18} />
        </button>
        <button
          onClick={() => share("facebook")}
          className="p-2 border rounded hover:bg-blue-50 transition"
          aria-label="Share on Facebook"
        >
          <Facebook size={18} />
        </button>
        <button
          onClick={() => share("copy")}
          className="p-2 border rounded hover:bg-gray-50 transition"
          aria-label="Copy link"
        >
          <LinkIcon size={18} />
        </button>
      </div>

      {/* ✅ Comments Section */}
      <div className="bg-gray-50 p-4 rounded-lg shadow">
        <Comments blogId={post.id} />
      </div>
    </div>
  );
}
